import { Component, Input, Output, EventEmitter, inject, DestroyRef, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';

/**
 * Modelo de nodo para el árbol de activos.
 * Superconjunto compatible con todas las variantes de la app:
 * - catalogId / catalogItemId → usado por flujo de incidencias
 * - type → usado por flujo de OT / maintenance-task-list
 */
export interface ActivoNode {
  id: string;
  name: string;
  code?: string;
  catalogId?: string;
  catalogItemId?: string;
  type?: 'location' | 'equipment';
  children?: ActivoNode[];
}

// ---------------------------------------------------------------------------
// Subcomponente recursivo para el árbol
// ---------------------------------------------------------------------------
@Component({
  selector: 'app-activo-tree',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['nodes', 'selectedId', 'expandedMap', 'searchTerm'],
  outputs: ['select'],
  template: `
    <ul class="space-y-0.5 sm:space-y-1">
      @for (node of nodes; track trackByNodeId($index, node)) {
        <li class="transition-all duration-200 min-w-0">
          <div class="flex items-center gap-2 sm:gap-3 group transition-all duration-200 px-2 py-2.5 sm:px-3 sm:py-3 cursor-pointer border border-transparent hover:border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:border-blue-400 min-w-0"
               [ngClass]="{
                 'bg-blue-100 border-blue-300 shadow-sm dark:bg-blue-900 dark:border-blue-400': selectedId === node.id,
                 'bg-gray-50 dark:bg-gray-900': node.children?.length && selectedId !== node.id,
                 'row-search-match': searchTerm && node.name.toLowerCase().includes(searchTerm.toLowerCase())
               }">

            <!-- Botón de expansión -->
            @if (node.children?.length) {
              <button (click)="toggle(node.id)"
                      class="focus:outline-none text-slate-600 hover:text-primary transition-colors p-0.5 sm:p-1 shrink-0"
                      [attr.aria-expanded]="isExpanded(node.id)"
                      [attr.aria-label]="(isExpanded(node.id) ? 'Contraer' : 'Expandir') + ' ' + node.name">
                <span class="material-icons text-base sm:text-lg transition-transform duration-200"
                      [ngClass]="{'rotate-90': isExpanded(node.id)}">
                  chevron_right
                </span>
              </button>
            }

            <!-- Espacio de alineación cuando no hay botón -->
            @if (!node.children?.length) {
              <div class="w-5 sm:w-6 shrink-0"></div>
            }

            <!-- Icono de categoría -->
            <span class="material-icons text-lg sm:text-xl shrink-0 text-primary dark:text-blue-400">
              {{ getNodeIcon(node) }}
            </span>

            <!-- Radio button -->
            <input type="radio"
                   [checked]="selectedId === node.id"
                   (change)="select.emit(node)"
                   class="w-4 h-4 shrink-0 text-primary border-2 border-slate-300 focus:ring-2 focus:ring-primary focus:ring-opacity-50 transition-all duration-150 hover:border-primary hover:border-opacity-60"
                   [id]="'activo-' + node.id"
                   name="activo-radio" />

            <!-- Label: min-w-0 + wrap-break-word para que el texto se ajuste en móvil -->
            <label [for]="'activo-' + node.id"
                   class="cursor-pointer flex-1 min-w-0 flex flex-col gap-0.5 text-slate-800 dark:text-gray-100 text-xs sm:text-sm select-none transition-colors group-hover:text-slate-900 dark:group-hover:text-white wrap-break-word"
                   [ngClass]="{
                     'text-primary dark:text-blue-400 font-semibold': selectedId === node.id,
                     'font-medium': node.children?.length
                   }">
              <span>{{ node.name }}</span>
              @if (node.code) {
                <span class="text-xs text-slate-500 dark:text-slate-400">{{ node.code }}</span>
              }
            </label>

            <!-- Badge de cantidad de hijos -->
            @if (node.children?.length) {
              <span class="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-200 rounded-full border border-slate-200 dark:border-gray-600 shrink-0">
                {{ node.children?.length }}
              </span>
            }
          </div>

          <!-- Subárbol: indentación reducida en móvil -->
          @if (node.children?.length && isExpanded(node.id)) {
            <div class="ml-4 mt-0.5 sm:ml-8 sm:mt-1 border-l-2 border-slate-200 dark:border-gray-700 pl-2 sm:pl-4 min-w-0">
              <app-activo-tree
                [nodes]="node.children || []"
                [selectedId]="selectedId"
                (select)="select.emit($event)"
                [expandedMap]="expandedMap"
                [searchTerm]="searchTerm"
              ></app-activo-tree>
            </div>
          }
        </li>
      }
    </ul>
  `
})
export class ActivoTreeComponent {
  nodes: ActivoNode[] = [];
  selectedId: string | null = null;
  expandedMap: Record<string, boolean> = {};
  select = new EventEmitter<ActivoNode>();
  searchTerm: string = '';

  isExpanded(id: string): boolean {
    return !!this.expandedMap[id];
  }

  toggle(id: string): void {
    this.expandedMap[id] = !this.expandedMap[id];
  }

  trackByNodeId(_index: number, node: ActivoNode): string {
    return node.id;
  }

  getNodeIcon(node: ActivoNode): string {
    return 'folder';
  }
}

// ---------------------------------------------------------------------------
// Drawer selector
// ---------------------------------------------------------------------------
@Component({
  selector: 'app-drawer-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ActivoTreeComponent, DrawerModule, ButtonModule],
  template: `
    <p-drawer
      [(visible)]="visible"
      (onHide)="onClose()"
      position="right"
      [modal]="true"
      [closeOnEscape]="true"
      styleClass="drawer-selector-asset !w-full !max-w-[100vw] sm:!w-[28rem] md:!w-[38rem] lg:!w-[46rem] xl:!w-[54rem]">

      <!-- Header: compacto en móvil -->
      <ng-template pTemplate="header">
        <div class="flex items-center gap-2 sm:gap-3 min-w-0">
          <span class="material-icons text-lg sm:text-xl shrink-0 text-primary dark:text-blue-400">playlist_add_check</span>
          <div class="flex flex-col min-w-0">
            <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 m-0 truncate">{{ title }}</h2>
            <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 m-0 truncate">{{ subtitle }}</p>
          </div>
        </div>
      </ng-template>

      <!-- Content -->
      <div class="flex flex-col h-full min-w-0">

        <!-- Búsqueda -->
        <div class="px-3 py-2.5 sm:px-4 sm:py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div class="relative">
            <span class="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">search</span>
            <input type="text"
                   [ngModel]="searchInput"
                   (ngModelChange)="onSearchInput($event)"
                   placeholder="Buscar activo por nombre..."
                   class="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-primary focus:ring-opacity-50 focus:border-primary transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
            @if (searchInput) {
              <button (click)="clearSearch()"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none cursor-pointer">
                <span class="material-icons text-lg">clear</span>
              </button>
            }
          </div>
          @if (searchTerm) {
            <div class="mt-2 flex items-center gap-1.5">
              <span class="inline-flex items-center gap-1 text-xs font-medium text-primary dark:text-blue-400 bg-primary/5 dark:bg-blue-500/10 px-2 py-1 rounded-md">
                <span class="material-icons text-sm">search</span>
                {{ getResultCount() }} resultado(s)
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400">• Coincidencias resaltadas</span>
            </div>
          }
        </div>

        <!-- Lista: @defer nativo de Angular para no bloquear la apertura del drawer -->
        <div class="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-4 sm:py-4 bg-white dark:bg-gray-800">
          @defer (when showTreeContent) {
            @if (filteredActivos.length) {
              <app-activo-tree
                [nodes]="filteredActivos"
                [selectedId]="selectedId"
                (select)="onSelect($event)"
                [expandedMap]="effectiveExpandedMap"
                [searchTerm]="searchTerm"
              ></app-activo-tree>
            } @else {
              <div class="flex flex-col items-center justify-center py-12 text-center">
                <span class="material-icons text-4xl text-gray-300 dark:text-gray-600 mb-3">search_off</span>
                <h3 class="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">No se encontraron activos</h3>
                <p class="text-gray-500 dark:text-gray-400 text-sm mb-4">Intenta con otros términos de búsqueda</p>
                @if (searchTerm) {
                  <button (click)="clearSearch()"
                          class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium cursor-pointer">
                    Limpiar búsqueda
                  </button>
                }
              </div>
            }
          } @placeholder {
            <div class="space-y-2 animate-pulse px-1 py-2">
              @for (row of skeletonRows; track row) {
                <div class="flex items-center gap-3 px-2 py-2.5">
                  <div class="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 shrink-0"></div>
                  <div class="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 shrink-0"></div>
                  <div class="h-4 rounded bg-gray-200 dark:bg-gray-700 flex-1" [style.width]="row + '%'"></div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Footer: en móvil se apila para que "Expandir todo" no se corte -->
        <div class="px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm shrink-0">
          <span class="text-gray-500 dark:text-gray-400">
            @if (searchTerm) {
              {{ getResultCount() }} de {{ getTotalCount() }} activo(s)
            } @else {
              {{ activos.length }} activo(s) disponible(s)
            }
          </span>
          <button (click)="expandAll()"
                  class="text-primary dark:text-blue-400 hover:text-primary-700 dark:hover:text-blue-300 font-medium transition-colors cursor-pointer text-left sm:text-right py-1 min-h-[44px] sm:min-h-0">
            {{ allExpanded ? 'Contraer todo' : 'Expandir todo' }}
          </button>
        </div>

      </div>
    </p-drawer>
  `,
  styles: [`
    /* Fila con coincidencia de búsqueda: borde izquierdo sutil */
    .row-search-match {
      border-left: 3px solid var(--color-primary);
      background-color: rgba(15, 76, 117, 0.04);
    }
    :host-context(.dark) .row-search-match {
      border-left-color: #4797c7;
      background-color: rgba(71, 151, 199, 0.08);
    }
  `]
})
export class DrawerSelectorComponent implements OnChanges {
  @Input() activos: ActivoNode[] = [];
  @Input() selectedId: string | null = null;
  @Input() visible = false;
  @Output() select = new EventEmitter<ActivoNode>();
  @Output() close = new EventEmitter<void>();
  @Input() title: string = 'Seleccionar Activo';
  @Input() subtitle: string = 'Elige el activo deseado';

  /** Se pone a true tras abrir el drawer para que @defer lo renderice sin bloquear la animación */
  showTreeContent = false;

  /** Anchos aleatorios fijos para el skeleton del placeholder */
  readonly skeletonRows = [70, 55, 85, 60, 75, 50, 65, 80, 55, 70];

  /** Valor mostrado en el input (actualización inmediata) */
  searchInput = '';
  /** Término usado para filtrar (debounced ~250ms para evitar lag al tipear) */
  searchTerm = '';
  expandedMap: Record<string, boolean> = {};

  private readonly searchSubject = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);

  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(term => {
      this.searchTerm = term;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const vis = changes['visible'];
    if (!vis) return;
    if (vis.currentValue === true) {
      this.showTreeContent = false;
      this.cdr.markForCheck();
      // Cede el hilo al navegador para que el drawer anime sin bloqueo,
      // luego @defer toma el control nativo del framework para renderizar el árbol.
      setTimeout(() => {
        this.showTreeContent = true;
        this.cdr.markForCheck();
      }, 16);
    } else {
      this.showTreeContent = false;
      this.cdr.markForCheck();
    }
  }

  onSearchInput(value: string): void {
    this.searchInput = value;
    this.searchSubject.next(value);
  }

  /** Al buscar, expande todos los nodos filtrados para mostrar resultados sin expandir manualmente */
  get effectiveExpandedMap(): Record<string, boolean> {
    if (!this.searchTerm.trim()) return this.expandedMap;
    const expandAll = (nodes: ActivoNode[]): Record<string, boolean> => {
      const map: Record<string, boolean> = {};
      nodes.forEach(node => {
        if (node.children?.length) {
          map[node.id] = true;
          Object.assign(map, expandAll(node.children));
        }
      });
      return map;
    };
    return { ...this.expandedMap, ...expandAll(this.filteredActivos) };
  }

  get filteredActivos(): ActivoNode[] {
    if (!this.searchTerm.trim()) return this.activos || [];
    const filter = (nodes: ActivoNode[] = []): ActivoNode[] =>
      (nodes || [])
        .map(node => {
          if (node.name.toLowerCase().includes(this.searchTerm.toLowerCase())) return node;
          if (node.children) {
            const filteredChildren = filter(node.children);
            if (filteredChildren.length) return { ...node, children: filteredChildren };
          }
          return null;
        })
        .filter(Boolean) as ActivoNode[];
    return filter(this.activos || []);
  }

  get allExpanded(): boolean {
    const getAllNodeIds = (nodes: ActivoNode[]): string[] =>
      nodes.reduce((ids: string[], node) => {
        if (node.children?.length) {
          ids.push(node.id);
          ids.push(...getAllNodeIds(node.children));
        }
        return ids;
      }, []);
    const allNodeIds = getAllNodeIds(this.activos);
    return allNodeIds.length > 0 && allNodeIds.every(id => this.expandedMap[id]);
  }

  clearSearch(): void {
    this.searchInput = '';
    this.searchTerm = '';
    this.searchSubject.next('');
    this.cdr.markForCheck();
  }

  getResultCount(): number {
    const count = (nodes: ActivoNode[]): number =>
      nodes.reduce((n, node) => n + 1 + (node.children ? count(node.children) : 0), 0);
    return count(this.filteredActivos);
  }

  getTotalCount(): number {
    const count = (nodes: ActivoNode[]): number =>
      nodes.reduce((n, node) => n + 1 + (node.children ? count(node.children) : 0), 0);
    return count(this.activos);
  }

  expandAll(): void {
    const getAllNodeIds = (nodes: ActivoNode[]): string[] =>
      nodes.reduce((ids: string[], node) => {
        if (node.children?.length) {
          ids.push(node.id);
          ids.push(...getAllNodeIds(node.children));
        }
        return ids;
      }, []);
    const shouldExpand = !this.allExpanded;
    getAllNodeIds(this.activos).forEach(id => { this.expandedMap[id] = shouldExpand; });
  }

  onSelect(node: ActivoNode): void {
    this.select.emit(node);
    this.onClose();
  }

  onClose(): void {
    this.close.emit();
  }
}
